import { defineStore } from 'pinia'
import type { Brand, Category, Product, ProductAttribute, ProductFacets, ProductFilterState, ProductModelOption } from '~/types'
import { applyUiSortToFilters, normalizeProductFilters, sortStateFromUiValue } from '~/utils/productFilters'

export const PRODUCTS_PAGE_SIZE = 16

export const useProductsStore = defineStore('products', () => {
  const { get } = useApi()

  const products = ref<Product[]>([])
  const featuredProducts = ref<Product[]>([])
  const currentProduct = ref<Product | null>(null)
  const categories = ref<Category[]>([])
  const brands = ref<Brand[]>([])
  const attributes = ref<ProductAttribute[]>([])
  const models = ref<ProductModelOption[]>([])
  const filterOptions = ref<{ colors: string[], storages: string[] }>({
    colors: [],
    storages: [],
  })
  const facets = ref<ProductFacets | null>(null)
  const facetsLoading = ref(false)
  const loading = ref(false)
  const loadingMore = ref(false)
  const featuredLoading = ref(false)
  const productsFetched = ref(false)
  const modelsLoading = ref(false)

  function defaultFilterState(): ProductFilterState {
    const settings = useState<Record<string, string>>('site-settings', () => ({}))
    return {
      page: 1,
      limit: PRODUCTS_PAGE_SIZE,
      ...sortStateFromUiValue(settings.value.products_default_sort),
    }
  }

  const filters = ref<ProductFilterState>(defaultFilterState())
  const pagination = ref({
    page: 1,
    limit: PRODUCTS_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  })

  const hasMoreProducts = computed(() => pagination.value.page < pagination.value.totalPages)

  function buildProductsQuery(params: ProductFilterState) {
    const query = new URLSearchParams()
    const arrayKeys = new Set([
      'brandIds',
      'models',
      'colors',
      'storages',
      'attributeValueIds',
      'availabilityStatusIds',
    ])

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return
      if (arrayKeys.has(key) && Array.isArray(value)) {
        if (value.length) query.set(key, value.join(','))
        return
      }
      if ((key === 'inStock' || key === 'onSale' || key === 'refurbishedOnly') && value === false) return
      if (Array.isArray(value)) return
      query.set(key, String(value))
    })
    return query
  }

  async function fetchProducts(
    customFilters?: ProductFilterState,
    options?: { append?: boolean },
  ) {
    const append = options?.append ?? false

    if (append) {
      if (!hasMoreProducts.value || loadingMore.value || loading.value) return
      loadingMore.value = true
    } else {
      loading.value = true
      products.value = []
    }

    try {
      const merged = {
        ...filters.value,
        ...customFilters,
        limit: PRODUCTS_PAGE_SIZE,
        page: append
          ? pagination.value.page + 1
          : (customFilters?.page ?? 1),
      }
      const apiParams = normalizeProductFilters(merged)
      filters.value = applyUiSortToFilters(merged)

      if (!append) void fetchFacets()

      const response = await get<Product[]>(`/products?${buildProductsQuery(apiParams).toString()}`)
      if (response.success && response.data) {
        products.value = append
          ? [...products.value, ...response.data]
          : response.data
        if (response.meta) {
          pagination.value = response.meta
        }
      }
    } finally {
      loading.value = false
      loadingMore.value = false
      productsFetched.value = true
    }
  }

  async function loadMoreProducts() {
    await fetchProducts(undefined, { append: true })
  }

  async function fetchFeatured() {
    featuredLoading.value = true
    try {
      const response = await get<Product[]>('/products?isFeatured=true&limit=8')
      if (response.success && response.data) {
        featuredProducts.value = response.data
      }
    } finally {
      featuredLoading.value = false
    }
  }

  async function fetchProduct(slug: string) {
    loading.value = true
    try {
      const response = await get<Product>(`/products/${slug}`)
      if (response.success && response.data) {
        const data = {
          ...response.data,
          specifications: parseProductSpecifications(response.data.specifications) ?? undefined,
        }
        currentProduct.value = data
        return data
      }
      return null
    } finally {
      loading.value = false
    }
  }

  async function fetchCategories() {
    const response = await get<Category[]>('/categories')
    if (response.success && response.data) {
      categories.value = response.data
    }
  }

  async function fetchCategory(slug: string) {
    const response = await get<Category & { products: Product[] }>(`/categories/${slug}`)
    return response.success ? response.data : null
  }

  async function fetchBrands() {
    const response = await get<Brand[]>('/brands')
    if (response.success && response.data) {
      brands.value = response.data
    }
  }

  async function fetchAttributes() {
    const response = await get<ProductAttribute[]>('/catalog/attributes')
    if (response.success && response.data) {
      attributes.value = response.data
    }
  }

  async function fetchModels(brandIds?: number[]) {
    if (!brandIds?.length) {
      models.value = []
      return
    }
    modelsLoading.value = true
    try {
      const response = await get<ProductModelOption[]>(
        `/catalog/product-models?brandIds=${brandIds.join(',')}`,
      )
      if (response.success && response.data) {
        models.value = response.data
      }
    } finally {
      modelsLoading.value = false
    }
  }

  async function fetchFilterOptions() {
    const response = await get<{ colors: string[], storages: string[] }>('/catalog/filter-options')
    if (response.success && response.data) {
      filterOptions.value = response.data
    }
  }

  async function fetchFacets(customFilters?: ProductFilterState) {
    facetsLoading.value = true
    try {
      const merged = { ...filters.value, ...customFilters }
      const apiParams = normalizeProductFilters(merged)
      const query = buildProductsQuery(apiParams)
      query.delete('page')
      query.delete('limit')
      query.delete('sortBy')
      query.delete('sortOrder')
      const response = await get<ProductFacets>(`/products/facets?${query.toString()}`)
      if (response.success && response.data) {
        facets.value = response.data
      }
    } finally {
      facetsLoading.value = false
    }
  }

  async function fetchBrand(slug: string) {
    const response = await get<Brand & { products: Product[] }>(`/brands/${slug}`)
    return response.success ? response.data : null
  }

  async function searchProducts(query: string) {
    return fetchProducts({ search: query, page: 1 })
  }

  function setFilters(newFilters: Partial<ProductFilterState>) {
    filters.value = { ...filters.value, ...newFilters }
  }

  function resetFilters() {
    filters.value = defaultFilterState()
  }

  /** Drop in-memory catalog cache when switching shops. */
  function clearCatalog() {
    products.value = []
    featuredProducts.value = []
    currentProduct.value = null
    categories.value = []
    brands.value = []
    attributes.value = []
    models.value = []
    filterOptions.value = { colors: [], storages: [] }
    facets.value = null
    facetsLoading.value = false
    loading.value = false
    loadingMore.value = false
    featuredLoading.value = false
    productsFetched.value = false
    modelsLoading.value = false
    resetFilters()
    pagination.value = {
      page: 1,
      limit: PRODUCTS_PAGE_SIZE,
      total: 0,
      totalPages: 0,
    }
  }

  return {
    products,
    featuredProducts,
    currentProduct,
    categories,
    brands,
    attributes,
    models,
    filterOptions,
    facets,
    facetsLoading,
    loading,
    loadingMore,
    featuredLoading,
    productsFetched,
    hasMoreProducts,
    modelsLoading,
    filters,
    pagination,
    fetchProducts,
    loadMoreProducts,
    fetchFeatured,
    fetchProduct,
    fetchCategories,
    fetchCategory,
    fetchBrands,
    fetchAttributes,
    fetchModels,
    fetchFilterOptions,
    fetchFacets,
    fetchBrand,
    searchProducts,
    setFilters,
    resetFilters,
    clearCatalog,
  }
})
